import isUndefined from 'lodash/isUndefined';
import i18n from '~shared/utils/i18n';

/**
 * Merges two selections and calulates the betable attribute.
 * @param {Selection} selection old selection
 * @param {Selection} update new selection
 * @param {Boolean} isMarketActive set to true if market is active
 * @return {Selection} Merged selection
 */
 export const updateSelection = (selection, update = {}, isMarketActive = true) => {
    const { active: isSelectionActive = false, suspended: isSelectionSuspended = true } = update;
    const betable = Boolean(isSelectionActive && !isSelectionSuspended && isMarketActive);

    return {...selection, ...update, betable };
};

/**
 * Searches selection object for a attribute representing the special odds value and returns it if it exists.
 * @param {Selection} selection selection
 * @returns {String} string representing special odds value.
 */
export const getSpecialOddsValue = (selection = {}) => {
    if (!selection) {
        return '';
    }
    const { special_odds_value, specialoddsvalue } = selection;
    if (specialoddsvalue === '_') { return ''; }

    return special_odds_value || specialoddsvalue || '';
};

/**
 * Checks type, selection_type and name attributes of given selection, in that order, to see if the selection is an over or under selection.
 * @param {selection} selection Selection to check.
 * @deprecated Should use the selectionorder field from table sportsbook.selection_type instead (should come from BE with the selection data)
 * @returns {Boolean} true if selections is over or under type selection.
 */
export const selectionIsOverUnder = (selection = {}) => {
    if (!selection) {
        return false;
    }
    const { type, selection_type } = selection;

    return /^(over|under)/i.test(type || selection_type);
};

/**
 * @param {Selection} selection selection object
 * @return {String} returns coresponding tranlation from selection_labels.<selection.type> or empty string if it doesn't exist.
 */
export const getSelectionLabelTranslation = (selection = {}) => {
    if (!selection) {
        return '';
    }
    const { type: _type, selection_type } = selection;
    const translationSelector = `selection_labels.${(_type || selection_type || 'default').toLowerCase()}`;
    const translation = i18n.t(translationSelector);

    return translation !== translationSelector ? translation : '';
};

/**
 * Returns what should be displayed as the selections label. In the case of over under selections this will
 * Prepend the translated coresponding O or U before the special odds value. Otherwise it will add a '+' character
 * to the special odds value if it is not negative.
 * @param {Selection} selection selection object
 * @param {Boolean|undefined} isOverUnder if you already know if the selection is an Over/Under you can pass it here so it's not calulated again.
 * @returns {String} string to be showen as the selections label
 */
export const getSelectionLabel = (selection, isOverUnder) => {
    const _isOverUnder = isUndefined(isOverUnder)
    ? selectionIsOverUnder(selection)
    : isOverUnder;

    const specialOddsValue = getSpecialOddsValue(selection);

    if(_isOverUnder) {
        return `${getSelectionLabelTranslation(selection)} ${getSpecialOddsValue(selection)}`;
    }
    else if(/^\d/.test(specialOddsValue)) {
        return `+${specialOddsValue}`;
    }

    return specialOddsValue;
};

/**
 * Converts array of selections to an object with selections as keys.
 * @param {Array} selectionArray array of selections
 * @returns {Object} object with selections id's as keys.
 */
export const selectionArrayToObject = ((selectionArray = []) => {
    return selectionArray.reduce((acc, selection) => ({
        ...acc,
        [Number(selection.id)]: selection,
    }), {});
});

/**
 * Converts object with selection id's as keys and selections as values to array.
 * @param {Object} selectionObject object with selection id's as keys
 * @returns {Array} array of selection
 */
export const selectionObjectToArray = ((selectionObject = {}) => {
    return Object.keys(selectionObject).map(selectionId => ({
        id: selectionId,
        ...selectionObject[selectionId],
    }));
});
